import AuthCallback from '../components/auth-callback/AuthCallback';
import AuxiliaryRouter from './auxiliary-router/AuxiliaryRouter';
import CartHeader from '../page-fragments/cart-menu/CartHeader';
import CartHeaderMobileContent from '../page-fragments/cart-menu/CartHeaderMobileContent';
import CartModal from '../components/cart-modal/CartModal';
import QuickEstimateModal from '../components/quick-estimate-modal/QuickEstimateModal';
import QuickEstimateThankYouModal from '../components/quick-estimate-modal/QuickEstimateThankYouModal';
import EquipmentSearchAutoSuggest from '../page-fragments/search-autosuggest/EquipmentSearchAutoSuggest';
import LocationBottomSheet from '../components/location-bottom-sheet/LocationBottomSheet';
import LocationModal from '../components/location-modal/LocationModal';
import LoginRedirect from '../page-fragments/login-redirect/LoginRedirect';
import MessageModal from '../components/message-modal/MessageModal';
import NavbarLocation from '../page-fragments/navbar-location-selector/NavbarLocation';
import PageLoader from './../components/page-overlay-loader/LoaderWithPageOverlay';
import renderer from './renderer';
import UserMenu from '../page-fragments/user-menu/UserMenu';
import UserMenuContent from '../page-fragments/user-menu/UserMenuContent';
import { useEffect } from 'react';
import {
  GenericRendererComponent,
  RendererComponentType,
  RendererComponentWithAuth,
  RendererSelectorType,
} from '../types/renderer';

// Every Webflow placeholder that should mount the search island must be listed here by id.
// Page-level embeds are easy to miss: /rent's search box silently stopped rendering because its
// id was not in this list (SC-553232). Tracked for a class-based refactor so this is self-service.
const SEARCH_AUTOSUGGEST_SELECTOR = [
  '#es-search-autosuggest',
  '#es-search-autosuggest-mobile',
  '#es-search-page-autosuggest',
  '#rent-page-search',
  '#es-rent-page-autosuggest',
].join(', ');

const componentWithAuth: RendererComponentWithAuth = {
  componentType: RendererComponentType.AUTH,
  selectorType: RendererSelectorType.ID,
  selector: 'esr-auth-placeholder',
  component: () => {
    return <AuthCallback />;
  },
};

const componentsRegister: GenericRendererComponent[] = [
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-user-dropdown-menu',
    component: () => {
      return <UserMenu />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-mobile-user-menu-content',
    component: () => {
      return <UserMenuContent />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-mobile-cart-menu-content',
    component: () => {
      return <CartHeaderMobileContent />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-navbar-location-button',
    component: () => {
      return <NavbarLocation />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-cart-header-dropdown',
    component: () => {
      return <CartHeader />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.CLASS,
    selector: 'esr-cart-modal',
    component: () => {
      return (
        <>
          <CartModal />
          <QuickEstimateModal />
          <QuickEstimateThankYouModal />
        </>
      );
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-location-modal',
    component: () => {
      return <LocationModal />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-location-bottomsheet',
    component: () => {
      return <LocationBottomSheet />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-page-overlay-loader',
    component: () => {
      return <PageLoader />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-message-modal',
    component: () => {
      return <MessageModal />;
    },
  },

  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ATTRIBUTE,
    selector: SEARCH_AUTOSUGGEST_SELECTOR,
    component: ({ id, show_close_button, search_toggle_id, is_mobile_nav_search_element }) => {
      return (
        id && (
          <EquipmentSearchAutoSuggest
            id={id}
            showCloseButton={show_close_button}
            searchToggleId={search_toggle_id}
            isMobileNavSearchElement={is_mobile_nav_search_element}
          />
        )
      );
    },
  },

  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-route-view',
    component: () => {
      return <AuxiliaryRouter />;
    },
  },
  {
    componentType: RendererComponentType.GENERIC,
    selectorType: RendererSelectorType.ID,
    selector: 'esr-login-placeholder',
    component: () => {
      return <LoginRedirect />;
    },
  },
];

function Essentials() {
  useEffect(() => {
    renderer([componentWithAuth, ...componentsRegister]);
  }, []);

  return null;
}

export default Essentials;
